/**
 * BaaS Stables wallet — list/detail, quotes, execute, and BRL balance via MobileService `/v1/ib/console/*` → BaaS.
 */

import {
  buildBaasCdnIconUrl,
  resolveBaasIconUrl,
  resolveCdnIconsBaseUrl,
} from "../Pages/Private/BaasConsole/Extrato/baasCdnIcons";
import { collectPinnedShellAccountIds } from "./baasAccountsApi";
import { fetchBaasApiAccessInfo } from "./baasApiApi";
import { baasProxy } from "./baasConsoleClient";
import { fetchBaasAccounts, isValutSegregationWalletInstance } from "./baasAccountsListApi";
import { fetchInternalTransferOptions } from "./baasInternalTransfersApi";
import { fetchBaasStablesTransactions } from "./baasStablesApi";
import { isVirtualInstanceShellAccount } from "./baasVirtualShells";

export const BAAS_STABLES_OPERATION_TYPES = ["buy", "sell", "transfer", "deposit"];
export const BAAS_STABLES_OPERATION_STATUSES = ["completed", "pending", "failed"];
export const BAAS_STABLES_WALLET_NETWORKS = ["Ethereum", "Polygon", "Solana", "Tron", "Base"];
export const BAAS_STABLES_WALLET_STATUSES = ["active", "pending", "blocked", "inactive"];
/** Assets accepted by BaaS `POST …/stables/accounts/{id}/wallets`. */
export const BAAS_STABLES_CREATE_ASSETS = ["USDC", "USDT", "EURC", "BRL1"];

/**
 * Canonical create-wallet networks by asset (StableAssetNetwork matrix).
 * Prefer `fetchBaasStablesAssetNetworks` when the API is available.
 */
export const BAAS_STABLES_CREATE_NETWORKS_BY_ASSET = {
  BRL1: ["Polygon"],
  USDT: ["Ethereum", "Tron", "Polygon"],
  USDC: ["Ethereum", "Polygon", "Solana"],
  EURC: ["Ethereum", "Base"],
};

export const getBaasStablesCreateNetworksForAsset = (asset) => {
  const symbol = String(asset || "").trim().toUpperCase();
  return BAAS_STABLES_CREATE_NETWORKS_BY_ASSET[symbol] || [];
};

/** Stablecoin wallet assets only — never fiat AccountBalance currencies (BRL, USD, …). */
export const BAAS_STABLE_WALLET_ASSETS = new Set(BAAS_STABLES_CREATE_ASSETS);

/** Catalog assets the given account does not already have a stables wallet for. */
export const getBaasMissingCreateAssetsForAccount = (wallets = [], accountId) => {
  if (!accountId) return [...BAAS_STABLES_CREATE_ASSETS];
  const accountKey = String(accountId).trim();
  if (!accountKey) return [...BAAS_STABLES_CREATE_ASSETS];

  const owned = new Set(
    (Array.isArray(wallets) ? wallets : [])
      .filter((wallet) => String(wallet?.accountId || "").trim() === accountKey)
      .map((wallet) => String(wallet?.asset || "").trim().toUpperCase())
      .filter((asset) => BAAS_STABLE_WALLET_ASSETS.has(asset)),
  );

  return BAAS_STABLES_CREATE_ASSETS.filter((asset) => !owned.has(asset));
};

/** True for stables wallet symbols (BRL1 ≠ fiat BRL). */
export const isBaasStableWalletAsset = (asset) =>
  BAAS_STABLE_WALLET_ASSETS.has(String(asset || "").trim().toUpperCase());

/**
 * Keep only StableBalance asset keys (USDC/USDT/EURC/BRL1).
 * Drops fiat BRL and any accidental AccountBalance currency codes.
 */
export const pickBaasStableBalances = (balances = {}) => {
  const next = {};
  Object.entries(balances || {}).forEach(([key, value]) => {
    const asset = String(key || "").trim().toUpperCase();
    if (!isBaasStableWalletAsset(asset)) return;
    next[asset] = value;
  });
  return next;
};

const envBaasPublicUrl = () =>
  (typeof process !== "undefined" && process.env?.REACT_APP_BAAS_PUBLIC_URL?.trim()) || "";

/**
 * Public BaaS origin for CDN icons (env override, else instance access info).
 * @param {object | string} [instance]
 * @returns {Promise<string>}
 */
export const resolveBaasPublicBaseUrl = async (instance) => {
  const fromEnv = envBaasPublicUrl().replace(/\/+$/, "");
  if (fromEnv) return fromEnv;
  if (!instance) return "";
  try {
    const info = await fetchBaasApiAccessInfo(instance);
    return String(info?.baasBaseUrl || "")
      .trim()
      .replace(/\/+$/, "");
  } catch {
    return "";
  }
};

/** EURC reuses USDC CDN art until a dedicated icon exists. */
export const resolveBaasStableIconSymbol = (symbol) => {
  const key = String(symbol || "")
    .trim()
    .toUpperCase();
  return key === "EURC" ? "USDC" : key || String(symbol || "").trim();
};

/**
 * Stables icons always resolve to Front Door
 * `https://cdn.valut.services/assets/icons/{symbol}.svg` (or `REACT_APP_CDN_ICONS_BASE`).
 * Accepts API `icon_url` only when it is already on that CDN; never leaves
 * relative `cdn/icons/...`, BaaS host, or FoxBit statics as the img src.
 *
 * @param {string | null | undefined} iconUrl
 * @param {string} [symbol]
 * @param {string} [baasBaseUrl] unused for primary CDN; kept for call-site compat
 * @returns {string | null}
 */
export const resolveBaasStableIconUrl = (iconUrl, symbol, baasBaseUrl) => {
  const iconSymbol = resolveBaasStableIconSymbol(symbol);
  const cdnBase = resolveCdnIconsBaseUrl();
  const resolved = resolveBaasIconUrl(iconUrl, { symbol: iconSymbol, baasBaseUrl });
  if (
    resolved &&
    cdnBase &&
    (resolved === cdnBase ||
      resolved.startsWith(`${cdnBase}/`) ||
      /^https:\/\/cdn\.valut\.services\/assets\/icons\//i.test(resolved))
  ) {
    // Remap leftover …/eurc.svg links to USDC art.
    if (/\/eurc\.svg(?:[?#]|$)/i.test(resolved)) {
      return buildBaasCdnIconUrl(iconSymbol, { baasBaseUrl }) || resolved;
    }
    return resolved;
  }
  const fromSymbol = buildBaasCdnIconUrl(iconSymbol, { baasBaseUrl });
  if (fromSymbol && /^https?:\/\//i.test(fromSymbol)) return fromSymbol;
  return fromSymbol || resolved || null;
};

/**
 * Front Door CDN icon URL for stables assets (`…/assets/icons/{symbol}.svg`).
 * Override with `REACT_APP_CDN_ICONS_BASE` when needed.
 * EURC resolves to the USDC asset.
 *
 * @param {string} symbol
 * @param {string} [baasBaseUrl] ignored for primary Front Door URL (compat)
 */
export const buildBaasStableCdnIconUrl = (symbol, baasBaseUrl) => {
  const url = buildBaasCdnIconUrl(resolveBaasStableIconSymbol(symbol), {
    baasBaseUrl,
  });
  return url || null;
};

const buildBalance = (available, blocked = 0) => ({
  available,
  blocked,
  total: available + blocked,
});

export const getBaasStablesWalletListAssets = (wallets = []) =>
  [...new Set(wallets.map((item) => item.asset).filter(Boolean))].sort();

/** @deprecated Prefer getBaasStablesWalletListAssets */
export const getMockBaasStablesWalletListAssets = getBaasStablesWalletListAssets;

const NETWORK_TO_CHAIN_ID = {
  Ethereum: "ethereum",
  Polygon: "polygon",
  Solana: "solana",
  Tron: "tron",
  Base: "base",
};

const NETWORK_TO_BAAS_CODE = {
  Ethereum: "ERC20",
  Polygon: "POLYGON",
  Solana: "SOLANA",
  Tron: "TRON",
  Base: "BASE",
};

const BAAS_CODE_TO_NETWORK_LABEL = Object.fromEntries(
  Object.entries(NETWORK_TO_BAAS_CODE).map(([label, code]) => [code, label]),
);

/** Display label with DB NetworkCode, e.g. `Polygon [POLYGON]`. */
export const formatBaasStablesNetworkLabel = (networkLabelOrCode) => {
  const raw = String(networkLabelOrCode || "").trim();
  if (!raw) return "";
  const asCode = raw.toUpperCase();
  if (BAAS_CODE_TO_NETWORK_LABEL[asCode]) {
    return `${BAAS_CODE_TO_NETWORK_LABEL[asCode]} [${asCode}]`;
  }
  const code = NETWORK_TO_BAAS_CODE[raw];
  if (code) return `${raw} [${code}]`;
  return raw;
};

export const pickDefaultBaasCreateNetwork = (networks, current) => {
  const list = Array.isArray(networks) ? networks : [];
  if (list.length === 0) return "";
  if (current && list.includes(current)) return current;
  return list[0];
};

/**
 * Fetch supported networks for an asset from Baas catalog.
 * Always intersected with the StableAssetNetwork create matrix for the asset.
 * @returns {Promise<string[]>} display labels (Ethereum, Polygon, …)
 */
export const fetchBaasStablesAssetNetworks = async (instance, asset) => {
  const symbol = String(asset || "")
    .trim()
    .toUpperCase();
  const fallback = getBaasStablesCreateNetworksForAsset(symbol);
  const allowed = new Set(fallback);
  if (!instance || !symbol) return fallback;

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: `v1/stables/assets/${encodeURIComponent(symbol)}/networks`,
    });
    const rows = Array.isArray(data?.networks)
      ? data.networks
      : Array.isArray(data?.Networks)
        ? data.Networks
        : [];
    const labels = rows
      .map((item) => {
        const code = String(
          item?.network_code || item?.networkCode || item?.code || ""
        )
          .trim()
          .toUpperCase();
        return BAAS_CODE_TO_NETWORK_LABEL[code] || null;
      })
      .filter((label) => label && allowed.has(label));
    // Preserve matrix order.
    const fromApi = fallback.filter((label) => labels.includes(label));
    return fromApi.length > 0 ? fromApi : fallback;
  } catch {
    return fallback;
  }
};

/** @deprecated Use fetchBaasStablesAssetNetworks */
export const fetchBaasAssetNetworks = fetchBaasStablesAssetNetworks;

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== "") return obj[key];
  }
  return undefined;
};

const computeExpiresInSeconds = (expiresAt) => {
  if (!expiresAt) return 300;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(1, Math.floor(ms / 1000));
};

const ASSET_TO_STABLE_ID = {
  USDC: "usdc",
  USDT: "usdt",
  EURC: "eurc",
  BRL1: "brl1",
};

const STABLE_ID_TO_ASSET = Object.fromEntries(
  Object.entries(ASSET_TO_STABLE_ID).map(([asset, id]) => [id, asset]),
);

const DEFAULT_STABLE_RATES = {
  usdc: 5.42,
  usdt: 5.41,
  eurc: 6.15,
  brl1: 1,
};

export const resolveBaasWalletStableAssetId = (wallet) =>
  ASSET_TO_STABLE_ID[String(wallet?.asset ?? "").toUpperCase()] ?? "usdc";

export const resolveBaasWalletChainId = (wallet) =>
  NETWORK_TO_CHAIN_ID[wallet?.network] ?? "polygon";

export const resolveBaasWalletNetworkCode = (wallet) =>
  NETWORK_TO_BAAS_CODE[wallet?.network]
  ?? String(wallet?.network ?? "POLYGON").toUpperCase();

/** @deprecated Sync fallback — returns 0. Prefer fetchBaasWalletBrlBalance. */
export const getMockBaasWalletBrlBalance = () => 0;

/**
 * Parse **fiat BRL** available from BaaS BalanceList payload.
 * Amounts are minor units (centavos) — same as IB `/accounts/balances` / formatCurrency.
 * Never matches BRL1 (stablecoin) — that lives on stables wallet balances.
 */
export const parseBaasBrlAvailableCents = (data) => {
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : [];
  const brlItem = items.find((item) => {
    const code = String(item.currency_code || item.currency || "").toUpperCase();
    // Exact "BRL" only — do not treat BRL1 / BRLx as fiat.
    return code === "BRL";
  });
  if (!brlItem) return 0;
  const raw = Number(brlItem.available ?? brlItem.available_amount ?? 0);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return Math.round(raw);
};

/**
 * Instance-pinned shells (operating / overdraft / program) — never use their
 * AccountBalance as stables wallet or Comprar pay-in saldo.
 */
const fetchPinnedShellAccountIdSet = async (instance) => {
  if (!instance) return new Set();
  try {
    const options = await fetchInternalTransferOptions(instance);
    return collectPinnedShellAccountIds(options);
  } catch {
    return new Set();
  }
};

const isBaasStablesEligibleAccount = (account, pinnedShellIds = new Set()) => {
  if (!account?.id) return false;
  if (pinnedShellIds.has(String(account.id).trim())) return false;
  if (isVirtualInstanceShellAccount(account)) return false;
  return true;
};

/** One Conta id → one wallets GET (duplicate multi-saldo Contas rows must not double-fetch). */
const dedupeEligibleAccountsById = (accounts = []) => {
  const byId = new Map();
  for (const account of accounts) {
    const id = account?.id != null ? String(account.id).trim() : "";
    if (!id || byId.has(id)) continue;
    byId.set(id, account);
  }
  return Array.from(byId.values());
};

/**
 * Collapse duplicate StableBalance rows (same `stbl_bal_*` / composite id).
 * Prefer the row with more non-zero qty when both sides match.
 */
export const dedupeStablesWalletsById = (wallets = []) => {
  const byId = new Map();
  for (const wallet of wallets) {
    const id = wallet?.id != null ? String(wallet.id).trim() : "";
    if (!id) continue;
    const previous = byId.get(id);
    if (!previous) {
      byId.set(id, wallet);
      continue;
    }
    const prevWeight =
      Math.abs(Number(previous?.balances?.available ?? 0)) +
      Math.abs(Number(previous?.balances?.blocked ?? 0)) +
      Math.abs(Number(previous?.balances?.future ?? 0));
    const nextWeight =
      Math.abs(Number(wallet?.balances?.available ?? 0)) +
      Math.abs(Number(wallet?.balances?.blocked ?? 0)) +
      Math.abs(Number(wallet?.balances?.future ?? 0));
    if (nextWeight > prevWeight) byId.set(id, wallet);
  }
  return Array.from(byId.values());
};

/**
 * Linked **fiat** account BRL available in centavos (Comprar pay-in only).
 * Do not use this as BRL1 / stables wallet balance.
 * Never reads Overdraft / Operating / Program reserve AccountBalance.
 */
export const fetchBaasWalletBrlBalance = async (instance, wallet) => {
  if (!instance || !wallet?.accountId) return 0;

  const accountId = String(wallet.accountId).trim();
  const pinnedShellIds = await fetchPinnedShellAccountIdSet(instance);
  if (
    pinnedShellIds.has(accountId) ||
    isVirtualInstanceShellAccount({
      id: accountId,
      alias: wallet.accountAlias,
      name: wallet.accountAlias,
      entityName: wallet.entityName,
    })
  ) {
    return 0;
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/accounts/${encodeURIComponent(accountId)}/balances`,
  });

  return parseBaasBrlAvailableCents(data);
};

export const buildBaasWalletAccountData = (wallet) => ({
  name: wallet?.entityName ?? "",
  document: wallet?.entityId ?? "",
  accountAlias: wallet?.accountAlias ?? "",
  accountNumber: wallet?.accountNumber ?? "",
});

export const buildBaasWalletStablesAsset = (wallet, marketQuotes = []) => {
  const assetId = resolveBaasWalletStableAssetId(wallet);
  const quote = marketQuotes.find(
    (item) => String(item.asset ?? item.id).toLowerCase() === assetId,
  );
  const rate = Number(quote?.price ?? quote?.rate ?? DEFAULT_STABLE_RATES[assetId] ?? 5.4);
  const priceString = String(quote?.price ?? quote?.rate ?? rate);
  const iconUrl = resolveBaasStableIconUrl(
    quote?.iconUrl ?? quote?.icon_url ?? wallet?.iconUrl ?? wallet?.icon_url ?? null,
    assetId,
  );

  return {
    id: assetId,
    label: STABLE_ID_TO_ASSET[assetId] ?? wallet?.asset ?? assetId.toUpperCase(),
    balance: wallet?.balances?.available ?? 0,
    rate,
    priceString,
    iconUrl,
    walletId: wallet?.id ?? null,
    network: wallet?.network ?? null,
  };
};

/**
 * Swap pickers / balance cards: only stables the account already holds (owned wallets),
 * never a full market catalog / invented counterpart.
 * Scopes to the selected wallet's entity (fallback: account), aggregates same asset across networks.
 *
 * @param {{ pinSelected?: boolean }} [options]
 *   pinSelected (default true): prefer De wallet's walletId/rate when aggregating — for pickers.
 *   Use pinSelected:false for balance cards so De/Para changes do not alter totals/order.
 */
export const buildBaasSwapAssetsFromWallets = (
  wallets = [],
  marketQuotes = [],
  selectedWallet = null,
  options = {},
) => {
  const pinSelected = options.pinSelected !== false;
  const active = (wallets ?? []).filter(
    (item) => !item?.status || item.status === "active",
  );

  let scoped = active;
  if (selectedWallet?.entityId) {
    const sameEntity = active.filter(
      (item) => item.entityId === selectedWallet.entityId,
    );
    if (sameEntity.length) scoped = sameEntity;
  } else if (selectedWallet?.accountId) {
    const sameAccount = active.filter(
      (item) => item.accountId === selectedWallet.accountId,
    );
    if (sameAccount.length) scoped = sameAccount;
  }

  if (selectedWallet && !scoped.some((item) => item.id === selectedWallet.id)) {
    scoped = [selectedWallet, ...scoped];
  }

  // Stable iteration so aggregates do not depend on De selection order.
  const scopedSorted = [...scoped].sort((a, b) => {
    const labelA = String(a?.asset || a?.id || "");
    const labelB = String(b?.asset || b?.id || "");
    const byLabel = labelA.localeCompare(labelB);
    if (byLabel !== 0) return byLabel;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });

  const byAssetId = new Map();
  for (const item of scopedSorted) {
    const asset = buildBaasWalletStablesAsset(item, marketQuotes);
    const prev = byAssetId.get(asset.id);
    if (!prev) {
      byAssetId.set(asset.id, asset);
      continue;
    }

    const preferSelected = Boolean(
      pinSelected && selectedWallet && item.id === selectedWallet.id,
    );
    byAssetId.set(asset.id, {
      ...prev,
      balance: (Number(prev.balance) || 0) + (Number(asset.balance) || 0),
      walletId: preferSelected ? asset.walletId : prev.walletId,
      network:
        prev.network && asset.network && prev.network !== asset.network
          ? null
          : prev.network || asset.network,
      ...(preferSelected
        ? {
            rate: asset.rate,
            priceString: asset.priceString,
            iconUrl: asset.iconUrl,
          }
        : {}),
    });
  }

  // Alphabetical order — never pin "De" first (keeps balance cards fixed when swapping).
  return [...byAssetId.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label)),
  );
};

/** StableAccount id for BaaS `wallet_id` fields (never fiat account / BRL1 balance id). */
export const resolveBaasApiWalletId = (wallet) =>
  wallet?.apiWalletId || wallet?.metadata?.blockchainWalletId || wallet?.id || null;

const resolveQuoteAmountMode = (operation, inputCurrency) => {
  if (operation === "sell" || operation === "swap" || operation === "withdraw") {
    return "quantity";
  }
  return String(inputCurrency || "").toLowerCase() === "stable" ? "quantity" : "amount";
};

const resolveQuoteSide = (operation) =>
  operation === "buy" ? "buy" : "sell";

const mapBaasQuoteResponseToUi = (data, {
  operation,
  wallet,
  inputCurrency,
  transaction = {},
  fromAsset = null,
  toAsset = null,
}) => {
  const quoteId = data?.quote_id || data?.quoteId || data?.id;
  const assetLabel = String(
    data?.asset || wallet?.asset || transaction?.assetLabel || "",
  ).toUpperCase();
  const assetId = ASSET_TO_STABLE_ID[assetLabel] ?? assetLabel.toLowerCase();
  const rate = Number(data?.rate ?? transaction?.rate ?? transaction?.price ?? 0);
  const brlAmount = Number(
    data?.gross_brl_amount ?? data?.grossBrlAmount ?? transaction?.brlAmount ?? 0,
  );
  const stableAmount = Number(
    data?.gross_stable_amount ?? data?.grossStableAmount ?? transaction?.stableAmount ?? 0,
  );
  const netBrlAmount = Number(
    data?.net_brl_amount ?? data?.netBrlAmount ?? transaction?.netBrlAmount ?? brlAmount,
  );
  const netStableAmount = Number(
    data?.net_stable_amount ?? data?.netStableAmount ?? transaction?.netStableAmount ?? stableAmount,
  );

  return {
    ...transaction,
    id: quoteId,
    quoteId,
    operation,
    assetId: fromAsset?.id ?? assetId,
    assetLabel: fromAsset?.label ?? assetLabel,
    fromAssetId: fromAsset?.id ?? (operation === "swap" ? assetId : undefined),
    toAssetId: toAsset?.id,
    fromAssetLabel: fromAsset?.label,
    toAssetLabel: toAsset?.label,
    inputCurrency,
    price: String(data?.rate ?? transaction?.price ?? rate ?? ""),
    rate,
    expiresAt: data?.expires_at ?? data?.expiresAt ?? null,
    expiresInSeconds: computeExpiresInSeconds(data?.expires_at ?? data?.expiresAt),
    walletId: resolveBaasApiWalletId(wallet),
    brlAmount,
    stableAmount,
    netBrlAmount,
    netStableAmount,
    feePercent: Number(data?.fee_percent ?? data?.feePercent ?? transaction?.feePercent ?? 0),
    feeBrl: Number(data?.fee_brl_amount ?? data?.feeBrlAmount ?? transaction?.feeBrl ?? 0),
    feeStable: Number(
      data?.fee_stable_amount ?? data?.feeStableAmount ?? transaction?.feeStable ?? 0,
    ),
    fromAmount: transaction?.fromAmount ?? (operation === "swap" ? stableAmount : undefined),
    toAmountGross: transaction?.toAmountGross,
    netFromAmount: transaction?.netFromAmount ?? (operation === "swap" ? netStableAmount : undefined),
    netToAmount: transaction?.netToAmount,
    rawQuote: data,
  };
};

/**
 * Create a live BaaS stables quote (`POST v1/stables/accounts/{id}/quotes`).
 * Settlement remains stubbed on the FoxBit provider; quotes/rates are real.
 */
export const createBaasStablesQuote = async ({
  instance,
  wallet,
  operation,
  amount,
  transaction,
  inputCurrency,
  fromAsset = null,
  toAsset = null,
}) => {
  if (!instance || !wallet?.accountId) {
    throw new Error("instance_and_wallet_required");
  }

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    throw new Error("invalid_quote_amount");
  }

  const walletId = resolveBaasApiWalletId(wallet);
  if (!walletId) {
    throw new Error("wallet_id_required");
  }

  const asset = String(
    fromAsset?.label || wallet?.asset || "",
  ).toUpperCase();
  const network = resolveBaasWalletNetworkCode(wallet);

  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/stables/accounts/${encodeURIComponent(wallet.accountId)}/quotes`,
    body: {
      wallet_id: walletId,
      asset,
      amount: numericAmount,
      network,
      side: resolveQuoteSide(operation),
      amount_mode: resolveQuoteAmountMode(operation, inputCurrency),
    },
  });

  return mapBaasQuoteResponseToUi(data, {
    operation,
    wallet,
    inputCurrency,
    transaction,
    fromAsset,
    toAsset,
  });
};

const buildExecuteBody = ({ operation, wallet, quote, toWallet = null }) => {
  const walletId = resolveBaasApiWalletId(wallet);
  const network = resolveBaasWalletNetworkCode(wallet);
  const asset = String(quote?.assetLabel || wallet?.asset || "").toUpperCase();
  const quoteId = quote?.quoteId || quote?.id || null;

  if (operation === "buy") {
    return {
      path: `v1/stables/accounts/${encodeURIComponent(wallet.accountId)}/purchases`,
      body: {
        wallet_id: walletId,
        asset,
        amount: Number(quote?.brlAmount ?? quote?.netBrlAmount ?? 0),
        fiat_currency: "BRL",
        network,
        quote_id: quoteId,
      },
    };
  }

  if (operation === "sell") {
    return {
      path: `v1/stables/accounts/${encodeURIComponent(wallet.accountId)}/sales`,
      body: {
        wallet_id: walletId,
        asset,
        amount: Number(quote?.stableAmount ?? quote?.netStableAmount ?? 0),
        fiat_currency: "BRL",
        network,
        quote_id: quoteId,
      },
    };
  }

  if (operation === "withdraw") {
    return {
      path: `v1/stables/accounts/${encodeURIComponent(wallet.accountId)}/withdrawals`,
      body: {
        wallet_id: walletId,
        asset,
        amount: Number(quote?.stableAmount ?? quote?.netStableAmount ?? 0),
        network,
        destination_address: String(quote?.destination || "").trim(),
        quote_id: quoteId,
      },
    };
  }

  if (operation === "swap") {
    const sourceAsset = String(
      quote?.fromAssetLabel || quote?.assetLabel || wallet?.asset || "",
    ).toUpperCase();
    const destinationAsset = String(
      quote?.toAssetLabel || toWallet?.asset || "",
    ).toUpperCase();
    if (!destinationAsset || destinationAsset === sourceAsset) {
      throw new Error("swap_destination_asset_required");
    }
    const destinationNetwork = toWallet
      ? resolveBaasWalletNetworkCode(toWallet)
      : network;
    const fromAmount = Number(
      quote?.fromAmount ?? quote?.stableAmount ?? quote?.netFromAmount ?? 0,
    );
    const destGross = Number(quote?.toAmountGross ?? quote?.netToAmount ?? 0);
    return {
      path: `v1/stables/accounts/${encodeURIComponent(wallet.accountId)}/conversions`,
      body: {
        wallet_id: walletId,
        source_asset: sourceAsset,
        destination_asset: destinationAsset,
        amount: fromAmount,
        destination_amount: destGross > 0 ? destGross : undefined,
        source_network: network,
        destination_network: destinationNetwork,
        quote_id: quoteId,
      },
    };
  }

  throw new Error(`unsupported_operation:${operation}`);
};

/**
 * Accept quote + create purchase/sale/withdrawal/conversion on BaaS.
 * Provider settlement (FoxBit submit) stays stubbed server-side.
 */
export const executeBaasStablesOperation = async ({
  instance,
  wallet,
  operation,
  quote,
  toWallet = null,
}) => {
  if (!instance || !wallet?.accountId) {
    throw new Error("instance_and_wallet_required");
  }

  const quoteId = quote?.quoteId || quote?.id;
  if (!quoteId) {
    throw new Error("quote_id_required");
  }

  await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/stables/quotes/${encodeURIComponent(quoteId)}/accept`,
  });

  const { path, body } = buildExecuteBody({ operation, wallet, quote, toWallet });
  const txn = await baasProxy({
    instance,
    method: "POST",
    baasPath: path,
    body,
  });

  const transactionId = txn?.transaction_id || txn?.transactionId || txn?.id;
  const status = String(txn?.status || "").toLowerCase();
  const uiStatus =
    status.includes("fail") || status.includes("cancel")
      ? "failed"
      : status.includes("complet")
        ? "completed"
        : "pending";

  return {
    transactionId,
    receipt: {
      transactionId,
      reference: txn?.external_id || transactionId,
      quoteId,
      operation,
      assetLabel: quote?.assetLabel ?? wallet?.asset,
      fromAssetLabel: quote?.fromAssetLabel,
      toAssetLabel: quote?.toAssetLabel,
      walletId: wallet?.id,
      walletAlias: wallet?.alias,
      entityName: wallet?.entityName,
      accountAlias: wallet?.accountAlias,
      stableAmount: quote?.stableAmount,
      brlAmount: quote?.brlAmount,
      netBrlAmount: quote?.netBrlAmount,
      netStableAmount: quote?.netStableAmount,
      fromAmount: quote?.fromAmount,
      netToAmount: quote?.netToAmount,
      destination: quote?.destination,
      chain: quote?.chain,
      status: uiStatus,
      initiatedBy: "backoffice",
      raw: txn,
    },
  };
};

const mapWalletStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("block") || value.includes("suspend")) return "blocked";
  if (value.includes("pend") || value.includes("process")) return "pending";
  if (value.includes("inactiv") || value.includes("fail")) return "inactive";
  return "active";
};

const mapNetworkLabel = (network) => {
  const value = String(network || "").toLowerCase();
  if (value.includes("poly")) return "Polygon";
  if (value.includes("sol")) return "Solana";
  if (value.includes("tron")) return "Tron";
  if (value.includes("eth")) return "Ethereum";
  return network || "Ethereum";
};

/** Read StableBalance qty fields (snake/camel; never AccountBalance cents). */
const readStableQty = (...candidates) => {
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

/**
 * Map BaaS stables wallet → UI. Balances come **only** from the stables wallet
 * `balance` object (StableBalance) — never from linked fiat `accounts/.../balances`.
 * BRL1 is a stablecoin asset; fiat BRL is fetched separately for Comprar.
 *
 * UI `id` prefers StableBalance `balance_id` so each asset (e.g. BRL1 vs USDC) is unique.
 * `apiWalletId` keeps StableAccount `wallet_id` (`stbl_acct_…`) for BaaS ops that need it.
 */
const mapStableWalletToUi = (wallet, account, baasBaseUrl = "") => {
  const rawBalance = wallet?.balance ?? wallet?.Balance ?? null;
  const stableBalance =
    rawBalance && typeof rawBalance === "object" && !Array.isArray(rawBalance)
      ? rawBalance
      : null;
  // StableBalance fields only — never account.balances / AccountBalance / transfer-options.
  const available = readStableQty(
    stableBalance?.available,
    stableBalance?.Available,
    wallet?.available,
  );
  const blocked = readStableQty(
    stableBalance?.blocked,
    stableBalance?.Blocked,
    wallet?.blocked,
  );
  const future = readStableQty(stableBalance?.future, stableBalance?.Future);
  const asset = String(
    wallet?.asset || stableBalance?.asset || stableBalance?.Asset || "USDC",
  )
    .trim()
    .toUpperCase();
  // Fiat BRL is never a stables wallet asset — skip mapping if mislabeled.
  if (!isBaasStableWalletAsset(asset)) {
    return null;
  }
  const apiIconUrl = wallet?.icon_url ?? wallet?.iconUrl ?? null;
  const apiWalletId =
    wallet?.wallet_id || wallet?.walletId || wallet?.WalletId || wallet?.id || null;
  const balanceId =
    stableBalance?.balance_id ||
    stableBalance?.balanceId ||
    stableBalance?.BalanceId ||
    wallet?.balance_id ||
    wallet?.balanceId ||
    null;
  // Prefer per-asset balance id; never collapse BRL1 into another asset's row.
  const id =
    balanceId ||
    (apiWalletId && asset ? `${apiWalletId}:${asset}` : apiWalletId);

  return {
    id,
    apiWalletId,
    balanceId,
    alias: `${asset} · ${account.alias || account.accountNumber || account.id}`,
    asset,
    iconUrl: resolveBaasStableIconUrl(apiIconUrl, asset, baasBaseUrl),
    network: mapNetworkLabel(wallet?.network),
    address: wallet?.address || "",
    status: mapWalletStatus(wallet?.status),
    entityId: account.entityId,
    entityName: account.entityName,
    instanceId: wallet?.instance_id ?? wallet?.instanceId ?? account.instanceId ?? null,
    instanceName:
      wallet?.instance_name ||
      wallet?.instanceName ||
      account.instanceName ||
      null,
    accountId: account.id,
    accountNumber: account.accountNumber,
    accountAlias: account.alias,
    balances: {
      ...buildBalance(available, blocked),
      future,
    },
    createdAt: wallet?.created_at || wallet?.createdAt || null,
    metadata: {
      blockchainWalletId: apiWalletId || "",
      providerRef: wallet?.address_hash || wallet?.addressHash || "",
      contractAddress: "",
      balanceId: balanceId || "",
    },
    recentOperations: [],
    raw: wallet,
  };
};

/** Resolve UI wallet by balance id, composite id, or legacy shared wallet_id. */
export const findBaasStablesWallet = (wallets, walletId) => {
  if (!walletId || !Array.isArray(wallets)) return null;
  const exact =
    wallets.find((item) => item.id === walletId) ||
    wallets.find((item) => item.balanceId === walletId);
  if (exact) return exact;
  // Legacy links used StableAccount wallet_id for every asset — only when unique.
  const byApi = wallets.filter((item) => item.apiWalletId === walletId);
  return byApi.length === 1 ? byApi[0] : null;
};

const EMPTY_LIMITS = {
  daily: { used: 0, max: 0 },
  monthly: { used: 0, max: 0 },
  items: [],
};

/** Completed txs that compose `used_cents` (same query as the teto sum). */
export const mapBaasStablesLimitUsage = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    id: String(row?.transaction_id ?? row?.transactionId ?? row?.id ?? ""),
    completedAt: row?.completed_at ?? row?.completedAt ?? null,
    asset: row?.asset ? String(row.asset) : null,
    usedCents: Number(row?.used_cents ?? row?.usedCents ?? 0) || 0,
  }));
};

/** Maps BaaS GET …/limits into UI cents shape. Keeps zeroed items so ops bars can render. */
export const mapBaasStablesLimitsResponse = (data) => {
  const apiItems = Array.isArray(data?.items) ? data.items : [];
  const mappedItems = apiItems.map((item) => {
    const used = Number(item?.used_cents ?? item?.usedCents ?? 0);
    const max = Number(item?.total_cents ?? item?.totalCents ?? 0);
    const explicitAvailable = item?.available_cents ?? item?.availableCents;
    const availableCents =
      explicitAvailable != null
        ? Math.max(0, Number(explicitAvailable) || 0)
        : Math.max(0, max - used);
    return {
      id: String(item?.id ?? ""),
      label: String(item?.label ?? item?.id ?? ""),
      description: String(item?.description ?? ""),
      used,
      max,
      availableCents,
      periodType: String(item?.period_type ?? item?.periodType ?? "").toLowerCase() || null,
      usage: mapBaasStablesLimitUsage(item?.usage),
    };
  });

  const policies = Array.isArray(data?.policies)
    ? data.policies.map((item) => ({
        operationType: String(item?.operation_type ?? item?.operationType ?? ""),
        periodType: String(item?.period_type ?? item?.periodType ?? ""),
        usedCents: Number(item?.used_cents ?? item?.usedCents ?? 0),
        limitCents: Number(
          item?.limit_cents ?? item?.limitCents ?? item?.limit_amount_cents ?? 0,
        ),
        availableCents: Number(item?.available_cents ?? item?.availableCents ?? 0),
      }))
    : [];

  const pickPeriod = (period) => {
    const fromPolicy = policies.find(
      (p) =>
        String(p.periodType).toLowerCase() === period &&
        String(p.operationType).toLowerCase() === "purchase",
    );
    if (fromPolicy && (fromPolicy.limitCents > 0 || fromPolicy.usedCents > 0)) {
      return { used: fromPolicy.usedCents, max: fromPolicy.limitCents };
    }
    const fromItem = mappedItems.find(
      (item) => item.id === "compra" && item.periodType === period,
    );
    if (fromItem) return { used: fromItem.used, max: fromItem.max };
    return null;
  };

  const purchaseItem =
    mappedItems.find((item) => item.id === "compra") ??
    mappedItems.find((item) => item.max > 0 || item.used > 0);

  const daily =
    pickPeriod("daily") ??
    pickPeriod("hourly") ?? {
      used: purchaseItem?.used ?? 0,
      max: purchaseItem?.max ?? 0,
    };

  const monthly =
    pickPeriod("monthly") ?? {
      used:
        policies.find(
          (p) =>
            String(p.periodType).toLowerCase() === "monthly" &&
            String(p.operationType).toLowerCase() === "purchase",
        )?.usedCents ?? daily.used,
      max:
        policies.find(
          (p) =>
            String(p.periodType).toLowerCase() === "monthly" &&
            String(p.operationType).toLowerCase() === "purchase",
        )?.limitCents ?? daily.max,
    };

  return {
    daily,
    monthly,
    items: mappedItems,
    policies,
    withdrawOnchain: data?.withdraw_onchain
      ? (() => {
          const used = Number(
            data.withdraw_onchain.used_cents ?? data.withdraw_onchain.usedCents ?? 0,
          );
          const max = Number(
            data.withdraw_onchain.total_cents ?? data.withdraw_onchain.totalCents ?? 0,
          );
          const explicit =
            data.withdraw_onchain.available_cents ?? data.withdraw_onchain.availableCents;
          return {
            id: String(data.withdraw_onchain.id ?? "saque-onchain"),
            label: String(data.withdraw_onchain.label ?? ""),
            description: String(data.withdraw_onchain.description ?? ""),
            used,
            max,
            availableCents:
              explicit != null
                ? Math.max(0, Number(explicit) || 0)
                : Math.max(0, max - used),
            usage: mapBaasStablesLimitUsage(data.withdraw_onchain.usage),
          };
        })()
      : null,
  };
};

const mapLimitsResponse = mapBaasStablesLimitsResponse;

const mapTransactionToRecentOp = (tx) => ({
  id: tx.id,
  type: tx.operation === "transfer" ? "transfer" : tx.operation,
  asset: tx.asset,
  from: tx.from || undefined,
  to: tx.to || undefined,
  toAsset: tx.to || undefined,
  amount: Number(tx.amountStable ?? tx.amount ?? 0),
  amountStable: Number(tx.amountStable ?? tx.amount ?? 0),
  amountBrl: Number(tx.amountBrl ?? 0),
  amountFrom: tx.amountFrom != null ? Number(tx.amountFrom) : undefined,
  amountTo: tx.amountTo != null ? Number(tx.amountTo) : undefined,
  rate: Number.isFinite(Number(tx.rate)) && Number(tx.rate) > 0 ? Number(tx.rate) : null,
  status:
    tx.status === "processed"
      ? "completed"
      : tx.status === "failed"
        ? "failed"
        : "pending",
  timestamp: tx.createdAt,
  reference: tx.referenceId,
  walletId: tx.walletId || null,
  balanceId: tx.balanceId || null,
  accountId: tx.accountId || null,
});

/** Match tx to a wallet by balance / wallet / account+asset — never OR-by-asset alone. */
const transactionMatchesWallet = (tx, wallet) => {
  if (!tx || !wallet) return false;
  const txBalanceId = String(tx.balanceId || "").trim();
  const walletBalanceId = String(wallet.balanceId || wallet.id || "").trim();
  if (txBalanceId && walletBalanceId && txBalanceId === walletBalanceId) return true;

  const txWalletId = String(tx.walletId || "").trim();
  const apiWalletId = String(wallet.apiWalletId || "").trim();
  const sameWallet = txWalletId && apiWalletId && txWalletId === apiWalletId;
  const sameAsset =
    String(tx.asset || "").toUpperCase() === String(wallet.asset || "").toUpperCase();
  if (sameWallet && sameAsset) return true;

  const sameAccount =
    tx.accountId && wallet.accountId && String(tx.accountId) === String(wallet.accountId);
  return Boolean(sameAccount && sameAsset);
};

const mapEntityWalletAggregate = (wallets, { limits = EMPTY_LIMITS, recentOperations = [] } = {}) => {
  if (!wallets.length) return null;

  const first = wallets[0];
  const balances = {};
  const iconUrls = {};
  wallets.forEach((wallet) => {
    // StableBalance per wallet/asset only — never fold linked fiat AccountBalance BRL into BRL1.
    const asset = String(wallet?.asset || "").trim().toUpperCase();
    if (!isBaasStableWalletAsset(asset)) return;

    if (!balances[asset]) {
      balances[asset] = buildBalance(0, 0);
    }
    const available = Number(wallet.balances?.available);
    const blocked = Number(wallet.balances?.blocked);
    balances[asset].available += Number.isFinite(available) ? available : 0;
    balances[asset].blocked += Number.isFinite(blocked) ? blocked : 0;
    // Always recompute total — never copy available into blocked/total from fiat.
    balances[asset].total = balances[asset].available + balances[asset].blocked;
    if (!iconUrls[asset]) {
      iconUrls[asset] =
        resolveBaasStableIconUrl(wallet.iconUrl, asset) ||
        buildBaasStableCdnIconUrl(asset);
    }
  });

  // Prefer account that holds the largest stable qty for limits scope.
  const preferredForLimits = [...wallets].sort((a, b) => {
    const totalA = Number(a.balances?.total ?? a.balances?.available ?? 0);
    const totalB = Number(b.balances?.total ?? b.balances?.available ?? 0);
    return totalB - totalA;
  })[0] || first;

  return {
    entityId: preferredForLimits.entityId ?? first.entityId,
    entityName: preferredForLimits.entityName ?? first.entityName,
    accountId: preferredForLimits.accountId ?? first.accountId,
    apiWalletId: preferredForLimits.apiWalletId ?? first.apiWalletId,
    balances,
    iconUrls,
    limits,
    recentOperations,
  };
};

/**
 * GET `v1/stables/accounts/{accountId}/limits` (optional `wallet_id`).
 * @returns {Promise<{ daily, monthly, items, withdrawOnchain }>}
 */
export const fetchBaasStablesAccountLimits = async (
  instance,
  accountId,
  { walletId = null } = {},
) => {
  if (!instance || !accountId) return EMPTY_LIMITS;
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/stables/accounts/${encodeURIComponent(accountId)}/limits`,
    query: walletId ? { wallet_id: walletId } : undefined,
  });
  return mapBaasStablesLimitsResponse(data);
};

/**
 * GET `v1/stables/accounts/{accountId}/limits/usage`
 * Same completed-ops query as UTILIZADO (`operation_type` + optional `period_type`).
 */
export const fetchBaasStablesLimitUsage = async (
  instance,
  accountId,
  { walletId = null, operationType, periodType = null } = {},
) => {
  if (!instance || !accountId || !operationType) {
    return { usedCents: 0, limitCents: 0, items: [], total: 0 };
  }
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/stables/accounts/${encodeURIComponent(accountId)}/limits/usage`,
    query: {
      operation_type: operationType,
      ...(walletId ? { wallet_id: walletId } : {}),
      ...(periodType ? { period_type: periodType } : {}),
    },
  });
  return {
    operationType: String(data?.operation_type ?? data?.operationType ?? operationType),
    periodType: String(data?.period_type ?? data?.periodType ?? periodType ?? ""),
    usedCents: Number(data?.used_cents ?? data?.usedCents ?? 0) || 0,
    limitCents: Number(data?.limit_cents ?? data?.limitCents ?? 0) || 0,
    items: mapBaasStablesLimitUsage(data?.items),
    total: Number(data?.total ?? data?.items?.length ?? 0) || 0,
  };
};

/**
 * PUT `v1/stables/accounts/{accountId}/limits` — requires `stables.limits.write`.
 * Body: `{ wallet_id?, policies: [{ operation_type, period_type, limit_amount_cents }] }`
 */
export const setBaasStablesAccountLimits = async (instance, accountId, body = {}) => {
  if (!instance || !accountId) {
    throw new Error("accountId is required");
  }
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/stables/accounts/${encodeURIComponent(accountId)}/limits`,
    body,
  });
};

/**
 * GET `v1/stables/accounts/{accountId}/limits/policies` — exact write-scope policies
 * (account or wallet). Soft-replace PUT must round-trip these.
 */
export const fetchBaasStablesLimitPolicies = async (
  instance,
  accountId,
  { walletId = null } = {},
) => {
  if (!instance || !accountId) {
    return { accountId: null, walletId: null, scope: "account", policies: [], total: 0 };
  }
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/stables/accounts/${encodeURIComponent(accountId)}/limits/policies`,
    query: walletId ? { wallet_id: walletId } : undefined,
  });
  const policies = Array.isArray(data?.policies)
    ? data.policies.map((item) => ({
        id: item?.id,
        operationType: String(item?.operation_type ?? item?.operationType ?? ""),
        periodType: String(item?.period_type ?? item?.periodType ?? ""),
        limitAmountCents: Number(
          item?.limit_amount_cents ?? item?.limitAmountCents ?? 0,
        ),
        active: item?.active !== false,
        updatedAt: item?.updated_at ?? item?.updatedAt ?? null,
      }))
    : [];
  return {
    accountId: data?.account_id ?? data?.accountId ?? accountId,
    walletId: data?.wallet_id ?? data?.walletId ?? walletId,
    scope: data?.scope ?? (walletId ? "wallet" : "account"),
    policies,
    total: Number(data?.total ?? policies.length),
  };
};

const fetchAccountLimits = async (instance, accountId, { walletId = null } = {}) => {
  if (!accountId) return EMPTY_LIMITS;
  try {
    return await fetchBaasStablesAccountLimits(instance, accountId, { walletId });
  } catch {
    return EMPTY_LIMITS;
  }
};

const fetchStablesWalletsBatches = async (
  instance,
  accounts,
  baasBaseUrl,
  pinnedShellIds,
) => {
  const eligibleAccounts = dedupeEligibleAccountsById(
    accounts.filter((account) => isBaasStablesEligibleAccount(account, pinnedShellIds)),
  );
  if (!eligibleAccounts.length) return [];

  const batches = await Promise.all(
    eligibleAccounts.map(async (account) => {
      try {
        const data = await baasProxy({
          instance,
          method: "GET",
          baasPath: `v1/stables/accounts/${encodeURIComponent(account.id)}/wallets`,
        });
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        return items
          .map((wallet) => mapStableWalletToUi(wallet, account, baasBaseUrl))
          .filter(Boolean);
      } catch {
        return [];
      }
    }),
  );

  // Same Conta fetched twice (multi-saldo Contas rows) → identical stbl_bal_* rows.
  return dedupeStablesWalletsById(batches.flat());
};

export const fetchBaasStablesWalletsList = async (instance) => {
  if (!instance) return [];

  const [accounts, baasBaseUrl, pinnedShellIds] = await Promise.all([
    fetchBaasAccounts(instance).catch(() => []),
    resolveBaasPublicBaseUrl(instance),
    fetchPinnedShellAccountIdSet(instance),
  ]);
  return fetchStablesWalletsBatches(instance, accounts, baasBaseUrl, pinnedShellIds);
};

/**
 * Valut matrix only: GET `v1/stables/peer-wallets`. Empty for partners / non-matrix pins.
 */
export const fetchBaasPeerStablesWallets = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) return [];
  try {
    const query = {};
    if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
      query.instance_id = Number(options.instanceId);
    }
    const [data, baasBaseUrl] = await Promise.all([
      baasProxy({
        instance,
        method: "GET",
        baasPath: "v1/stables/peer-wallets",
        query,
      }),
      resolveBaasPublicBaseUrl(instance),
    ]);
    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    return items
      .map((wallet) =>
        mapStableWalletToUi(
          wallet,
          {
            id: wallet?.account_id || wallet?.accountId || "",
            alias: wallet?.account_alias || wallet?.accountAlias || "",
            accountNumber: wallet?.account_number || wallet?.accountNumber || "",
            entityId: wallet?.entity_id || wallet?.entityId || "",
            entityName: wallet?.entity_name || wallet?.entityName || "",
            instanceId: wallet?.instance_id ?? wallet?.instanceId ?? null,
            instanceName: wallet?.instance_name || wallet?.instanceName || "",
          },
          baasBaseUrl,
        ),
      )
      .filter(Boolean);
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/stables/peer-wallets failed", error);
    }
    return [];
  }
};

/**
 * GET `v1/stables/accounts/{accountId}/wallets` for a known account set
 * (entity detail — avoids instance-wide account listing).
 * Dedupes by Conta id before fetch and by `stbl_bal_*` after, so card counts stay unique.
 *
 * @param {object} instance
 * @param {array} accounts mapped Contas rows
 */
export const fetchBaasStablesWalletsForAccounts = async (instance, accounts = []) => {
  if (!instance || !Array.isArray(accounts) || accounts.length === 0) return [];

  const [baasBaseUrl, pinnedShellIds] = await Promise.all([
    resolveBaasPublicBaseUrl(instance),
    fetchPinnedShellAccountIdSet(instance),
  ]);
  return fetchStablesWalletsBatches(instance, accounts, baasBaseUrl, pinnedShellIds);
};

/**
 * Create a stables wallet via BaaS `POST v1/stables/accounts/{accountId}/wallets`.
 * @returns {Promise<object>} mapped UI wallet
 */
export const createBaasStablesWallet = async (instance, { accountId, asset, network } = {}) => {
  if (!instance || !accountId || !asset) {
    throw new Error("accountId and asset are required");
  }

  const [accounts, baasBaseUrl, pinnedShellIds] = await Promise.all([
    fetchBaasAccounts(instance).catch(() => []),
    resolveBaasPublicBaseUrl(instance),
    fetchPinnedShellAccountIdSet(instance),
  ]);
  const account = accounts.find((item) => item.id === accountId);
  if (!account || !isBaasStablesEligibleAccount(account, pinnedShellIds)) {
    throw new Error("account_not_found");
  }

  const body = {
    asset: String(asset).trim().toUpperCase(),
  };
  if (network) {
    body.network = NETWORK_TO_BAAS_CODE[network] ?? String(network).toUpperCase();
  }

  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/stables/accounts/${encodeURIComponent(accountId)}/wallets`,
    body,
  });

  return mapStableWalletToUi(data?.wallet ?? data, account, baasBaseUrl);
};

export const fetchBaasStablesWalletById = async (instance, walletId) => {
  if (!instance || !walletId) return null;

  const trimmedId = String(walletId).trim();
  let wallet = null;

  // Prefer dedicated balance endpoint when id is stbl_bal_*.
  if (/^stbl_bal_/i.test(trimmedId)) {
    try {
      const [data, accounts, baasBaseUrl] = await Promise.all([
        baasProxy({
          instance,
          method: "GET",
          baasPath: `v1/stables/balances/${encodeURIComponent(trimmedId)}`,
        }),
        fetchBaasAccounts(instance).catch(() => []),
        resolveBaasPublicBaseUrl(instance),
      ]);
      const payload = data?.wallet ?? data;
      const accountId = payload?.account_id || payload?.accountId;
      const account =
        accounts.find((item) => item.id === accountId) ||
        (accountId
          ? {
              id: accountId,
              alias: accountId,
              accountNumber: "",
              entityId: "",
              entityName: "",
            }
          : null);
      if (account && payload) {
        wallet = mapStableWalletToUi(payload, account, baasBaseUrl);
      }
    } catch {
      wallet = null;
    }
  }

  const [wallets, transactions] = await Promise.all([
    wallet ? Promise.resolve([]) : fetchBaasStablesWalletsList(instance),
    fetchBaasStablesTransactions(instance).catch(() => []),
  ]);
  if (!wallet) {
    wallet = findBaasStablesWallet(wallets, trimmedId);
  }
  if (!wallet) return null;

  const recentOperations = transactions
    .filter((tx) => transactionMatchesWallet(tx, wallet))
    .slice(0, 10)
    .map(mapTransactionToRecentOp);

  return {
    ...wallet,
    recentOperations,
  };
};

export const fetchBaasStablesWalletEntities = async (instance) => {
  const wallets = await fetchBaasStablesWalletsList(instance);
  const seen = new Map();
  wallets.forEach((wallet) => {
    if (!seen.has(wallet.entityId)) {
      seen.set(wallet.entityId, {
        entityId: wallet.entityId,
        entityName: wallet.entityName,
      });
    }
  });
  return Array.from(seen.values());
};

export const fetchBaasStablesWallet = async (
  instance,
  entityId = null,
  { walletId = null } = {},
) => {
  const [wallets, transactions] = await Promise.all([
    fetchBaasStablesWalletsList(instance),
    fetchBaasStablesTransactions(instance).catch(() => []),
  ]);
  const filtered = entityId
    ? wallets.filter((wallet) => wallet.entityId === entityId)
    : wallets;
  if (!filtered.length) return null;

  // Prefer the wallet/balance with funds when selecting limits scope.
  const preferred =
    (walletId && findBaasStablesWallet(filtered, walletId)) ||
    [...filtered].sort((a, b) => {
      const totalA = Number(a.balances?.total ?? a.balances?.available ?? 0);
      const totalB = Number(b.balances?.total ?? b.balances?.available ?? 0);
      return totalB - totalA;
    })[0] ||
    filtered[0];
  const accountId = preferred.accountId;
  const apiWalletId = preferred.apiWalletId || null;
  const limits = await fetchAccountLimits(instance, accountId, {
    walletId: apiWalletId,
  });
  const recentOperations = transactions
    .filter((tx) => {
      if (walletId) {
        const scoped = findBaasStablesWallet(filtered, walletId) || preferred;
        return transactionMatchesWallet(tx, scoped);
      }
      if (!accountId) return true;
      return (
        String(tx.accountId || "") === String(accountId) ||
        filtered.some((w) => transactionMatchesWallet(tx, w))
      );
    })
    .slice(0, 10)
    .map(mapTransactionToRecentOp);

  return mapEntityWalletAggregate(filtered, { limits, recentOperations });
};
